import React, { useEffect } from 'react';
import { Box, Skeleton, Typography, Button } from '@mui/material';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import NewsCard from './NewsCard';
import NewsFeedAd from './NewsFeedAd';
import { fetchNews, fetchMyNews } from '../../services/newsApi';
import type { NewsArticle, NewsPage } from '../../types/News';

interface NewsFeedProps {
  tab: 'trending' | 'latest' | 'mine';
  sources: string[];
  bookmarkedIds: Set<number>;
  seenIds: Set<number>;
  onToggleBookmark: (article: NewsArticle) => void;
  onOpenRelated: (articleId: number) => void;
  onOpenDetail: (articleId: number) => void;
  /**
   * 読み込み済みの内容を親へ知らせる。
   *
   * 親が queryClient.getQueryData() で直接覗くと購読が無く、
   * 無限スクロールで増えても親が再描画されないため件数が古いままになる。
   */
  onLoadedChange?: (loaded: { articleIds: number[]; pages: number }) => void;
  /** 媒体を全部オフにしている状態。取得せず案内を出す */
  allMuted?: boolean;
  onEnableAll?: () => void;
}

const PAGE_SIZE = 24;

// 何件ごとに広告を挟むか。1ページ24件なので、詰めすぎない密度にしている
const AD_INTERVAL = 8;

// カード幅の下限だけ決めて、あとは入るだけ並べる。
// ブレークポイントごとの列数を持たなくて済み、PWAの狭い幅でも破綻しない
const GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: { xs: 1.25, sm: 1.75 },
} as const;

const NewsFeed: React.FC<NewsFeedProps> = ({
  tab,
  sources,
  bookmarkedIds,
  seenIds,
  onToggleBookmark,
  onOpenRelated,
  onOpenDetail,
  onLoadedChange,
  allMuted,
  onEnableAll,
}) => {
  const { ref, inView } = useInView({ rootMargin: '600px' });

  const query = useInfiniteQuery<NewsPage>({
    // 媒体フィルタを変えたら別クエリとして扱う
    queryKey: ['news', tab, sources.join(',')],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      tab === 'mine'
        ? fetchMyNews(pageParam as number, PAGE_SIZE, sources)
        : fetchNews({ tab, sources, page: pageParam as number, pageSize: PAGE_SIZE }),
    getNextPageParam: (lastPage) => (lastPage.has_next ? lastPage.page + 1 : undefined),
    // **無限クエリの再取得は、読み込み済みの全ページを順に取り直す。**
    // 20ページ読んだ状態で古くなっていると、戻ってきた瞬間に20リクエスト飛ぶ。
    // サーバー側の話題スナップショットも5分キャッシュなので、そこに合わせる。
    // ゲーム詳細を見て戻る程度の時間なら、これで再取得が起きない
    staleTime: 5 * 60 * 1000,
    // ゲーム詳細へ移動して戻ったときにスクロール位置を復元するには、
    // 読み込み済みの全ページがキャッシュから即座に描画されて
    // 同じ高さに戻る必要がある。既定の5分では戻るまでに消えることがある。
    // 参照: Docs/react-query-scroll-position.md
    gcTime: 30 * 60 * 1000,
    // 媒体フィルタを変えた瞬間に一覧が消えると、高さが変わって
    // スクロール位置が飛ぶ。新しい結果が来るまで前の結果を出しておく
    placeholderData: keepPreviousData,
    enabled: !allMuted,
  });

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  // キャッシュがあるときはスケルトンを出さない。
  // 出すとレイアウトが変わって ScrollRestoration が効かなくなる
  // (Docs/react-query-scroll-position.md)
  const showSkeleton = isLoading && !data;

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // 読み込み内容が変わるたびに親へ知らせる。
  // data は取得のたびに別オブジェクトになるので、これで過不足なく発火する
  useEffect(() => {
    if (!onLoadedChange) return;
    onLoadedChange({
      articleIds: data?.pages.flatMap((p) => p.results.map((a) => a.id)) ?? [],
      pages: data?.pages.length ?? 0,
    });
  }, [data, onLoadedChange]);

  if (allMuted) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.65rem', sm: '0.7rem' }, mb: 1.5 }}>
          表示する媒体が選ばれていません
        </Typography>
        <Button size="small" variant="outlined" onClick={onEnableAll}>
          すべて表示に戻す
        </Button>
      </Box>
    );
  }

  if (showSkeleton) {
    return (
      <Box sx={GRID}>
        {[...Array(8)].map((_, i) => (
          <Box key={i}>
            <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '16/9', borderRadius: 3 }} />
            <Skeleton width="90%" height={18} sx={{ mt: 1 }} />
            <Skeleton width="55%" height={18} />
          </Box>
        ))}
      </Box>
    );
  }

  if (isError && !data) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.65rem', sm: '0.7rem' }, mb: 1.5 }}>
          ニュースの取得に失敗しました
        </Typography>
        <Button size="small" variant="outlined" onClick={() => query.refetch()}>
          再読み込み
        </Button>
      </Box>
    );
  }

  const articles = data?.pages.flatMap((p) => p.results) ?? [];
  const lastPage = data?.pages[data.pages.length - 1];

  if (articles.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
          {tab === 'mine'
            ? 'ライブラリのゲームに関するニュースはまだありません'
            : 'ニュースがありません'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={GRID}>
        {articles.map((article, index) => (
          <React.Fragment key={article.id}>
            <NewsCard
              article={article}
              bookmarked={bookmarkedIds.has(article.id)}
              seen={seenIds.has(article.id)}
              onToggleBookmark={onToggleBookmark}
              onOpenRelated={onOpenRelated}
              onOpenDetail={onOpenDetail}
            />
            {/* 一定間隔で広告を挟む。グリッドの1マスを占めるので列崩れしない。
                先頭に来ないよう index+1 で判定する */}
            {(index + 1) % AD_INTERVAL === 0 && (
              <NewsFeedAd key={`ad-${article.id}`} />
            )}
          </React.Fragment>
        ))}
      </Box>

      {/* 上限で止まったときは黙って終わらせない。
          「読み込みが止まった？」と誤解されるため */}
      {!hasNextPage && lastPage?.limit_reached && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: { xs: '0.55rem', sm: '0.6rem' },
          }}>
            表示できる上限に達しました
          </Typography>
          {/*  アーカイブ溜まるまでは出さない
          <Typography sx={{
            color: 'rgba(255,255,255,0.28)',
            fontSize: { xs: '0.5rem', sm: '0.55rem' },
            mt: 0.5,
          }}>
            これより前の記事はゲーム詳細ページのニュースフィードからご覧ください
          </Typography> */}
        </Box>
      )}

      <Box ref={ref} sx={{ height: 1, mt: 2 }} />
      {isFetchingNextPage && (
        <Box sx={{ ...GRID, mt: { xs: 1.25, sm: 1.75 } }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              sx={{ width: '100%', aspectRatio: '16/9', borderRadius: 3 }}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default NewsFeed;
